import type { IMachineContext } from '@/types';
import {
  TAltPlayerOptions,
  IConfirmationInput,
  IMachineContextCurrentPlay,
  INextEvent,
  IPlayflowPlay,
  ISelectionButton,
  IStateMetaData,
  ITwoRowGenericSelectorInp,
  IYardageSelectorInput,
  TColors,
  TInputTypes,
  TKickoffEvents,
  TMachineEvents,
  TMetaFuncInput,
  TOffDef,
  TPatEvents,
  TPlayResults,
  TPlays,
  TRefEventsActions,
  TRegularPlayEvents,
  TTeam,
  TTeamSide,
  TUserRole,
  TWorkflowType,
} from '@/types';
import type { usePlayFlowState } from '@/stores/playFlow';
import {
  KICKOFF_YARDLINE,
  PAT_YARDLINE,
  PENALTY_INPUTS,
  POSSESSION_INPUTS,
  PRIMARY_FLOW_INPUTS,
  SAFTEY_KICKOFF_YARDLINE,
  TEventTypes,
  TOV_INPUT,
  TPlayDesigns,
  TPlayerRole,
  TPlayResultTypes,
  TQuarters,
  TRefEventOutcomes,
  YARDAGE_SELECTOR_BUTTONS,
  YARDAGE_SELECTOR_TOGGLE_PROPRITY,
} from '@/constants';
import { useGeneralState } from '@/stores/general';
import {
  FIRST_DOWN_BTN,
  KICKOFF_INPUTS,
  PAT_INPUTS,
  SAFTEY_BTN,
  TOUCHDOWN_BTN,
  TURNOVER_BTN,
} from '@/constants/machine';
import { cloneDeep, last, remove } from 'lodash';
import {
  getResultingDownAndDistance,
  isPreSnapKickoffPenalty,
  isReKickoffPenalty,
} from '@/stores/util/playFlowHelper';
import { useUserStore } from '@/stores/user';
import {
  isMultiTovPlay,
  fumbleLostResults,
  turnoverResults,
  touchdownResults,
} from '@/utilities/playResultTypes';

export const isTurnoverResult = (resultId: number): boolean => turnoverResults.includes(resultId);

export const containsTurnoverResult = (resultIds: number[]): boolean =>
  resultIds.some((id) => turnoverResults.includes(id));

export const isFumbleLostResult = (resultId: number): boolean => fumbleLostResults.includes(resultId);

export const containsFumbleLostResult = (resultIds: number[]): boolean =>
  resultIds.some((id) => fumbleLostResults.includes(id));

export const checkIsKickoffPunt = (context: IMachineContext): boolean =>
  context.currentWorkflow === TWorkflowType.KICKOFF || context.currentWorkflow === TWorkflowType.PUNT;

/**
 * Turnover's can be "chained" in a loop if multiple happen on a play. If there's
 * an even amount of tov events, assume ball ended up back with the offense, so
 * no turnover.
 */
export const getTurnoverStatus = (currentPlay: IMachineContextCurrentPlay): boolean => {
  if (!isTurnoverResult(last(currentPlay.resultType))) {
    return false;
  }

  const tovResults = currentPlay.resultType.filter((type) => isTurnoverResult(type));

  if (tovResults.length === 1) {
    return true;
  }

  const isDefRecovery = tovResults.length % 2 === 1;
  return isDefRecovery;
};

export const sendPenaltyEvent = (
  playFlowState: ReturnType<typeof usePlayFlowState>,
  generalState: ReturnType<typeof useGeneralState>,
  send: Function,
  currentPlay: IMachineContextCurrentPlay,
  evt: string,
  backToEvt: string
) => {
  send(evt, {
    ctxUpdate: {
      currentPlay: {
        ...currentPlay,
        refEventOutcomeId: TRefEventOutcomes.PENALTY,
      },
    },
    nextStateInput: {
      ...PENALTY_INPUTS(
        playFlowState.currentTeamRoles.offense,
        generalState.teams[playFlowState.currentTeamRoles.offense].logoUrl,
        playFlowState.currentTeamRoles.defense,
        generalState.teams[playFlowState.currentTeamRoles.defense].logoUrl
      ),
      backTo: backToEvt,
    } as ITwoRowGenericSelectorInp,
  });
};

export const getPostTouchdownResultType = (context: IMachineContext) => {
  const resultType: number[] = context.currentPlay?.resultType;

  if (isMultiTovPlay(resultType)) {
    const tovResults = resultType.filter((rt) => isTurnoverResult(rt));
    const lastTov = tovResults.pop();
    if (lastTov === TPlayResultTypes.INTERCEPTION) {
      tovResults.push(TPlayResultTypes.INTERCEPTION_TD);
    } else if (lastTov === TPlayResultTypes.FUMBLE_LOST) {
      tovResults.push(TPlayResultTypes.FUMBLE_LOST_TD);
    }
    return tovResults;
  }

  if (context.currentPlay?.playDesignId === TPlayDesigns.PASS && !context.currentPlay.qbScramble) {
    if (containsFumbleLostResult(resultType)) {
      return resultType.includes(TPlayResultTypes.SACK) ||
        resultType.includes(TPlayResultTypes.SACK_FUMBLE_LOST)
        ? [TPlayResultTypes.SACK_FUMBLE_LOST_TD]
        : [TPlayResultTypes.PASS_COMP_FUMBLE_LOST_TD];
    }
    if (resultType.includes(TPlayResultTypes.INTERCEPTION)) {
      return [TPlayResultTypes.INTERCEPTION_TD];
    }
    return [TPlayResultTypes.PASS_COMP_TD];
  }
  if (context.currentPlay?.playDesignId === TPlayDesigns.RUSH || !!context.currentPlay.qbScramble) {
    if (containsFumbleLostResult(resultType)) {
      return resultType.includes(TPlayResultTypes.RUSH_ATTENPT) ||
        resultType.includes(TPlayResultTypes.RUSH_ATTEMPT_FUMBLE_LOST)
        ? [TPlayResultTypes.RUSH_ATTEMPT_FUMBLE_LOST_TD]
        : [TPlayResultTypes.FUMBLE_LOST_TD];
    }
    return [TPlayResultTypes.RUSH_ATTEMPT_TD];
  }
  if (resultType.includes(TPlayResultTypes.FUMBLE_LOST)) {
    return [TPlayResultTypes.FUMBLE_LOST_TD];
  }

  return [...(context.currentPlay?.resultType ?? []), TPlayResultTypes.TD];
};

export const handlePostTouchdownPlay = (inp: TMetaFuncInput, offPuntOrKick = false, offTov = false): void => {
  const { send, playFlowState, context } = inp;
  const ctxUpdate: Partial<IMachineContext> = {
    currentPlay: {
      ...context.currentPlay,
      resultType: getPostTouchdownResultType(context),
      options: {
        ...context.currentPlay?.options,
        isScore: true,
        isKickoffOrPunt: offPuntOrKick,
        isTov: offTov,
      },
    },
  };

  const nextStateInput = {
    topOptions: [
      {
        text: TPatEvents.TWO_PT_ATTEMPT,
        value: TPatEvents.TWO_PT_ATTEMPT,
        color: TColors.INDIGO,
        col: 6,
      },
    ],
    bottomOptions: [
      {
        text: TPlays.REF,
        value: TPlays.REF,
        color: TColors.YELLOW,
        col: 4,
      },
    ],
    bottomJustify: 'end',
  } as ITwoRowGenericSelectorInp;

  // if overtime
  if (playFlowState.currentQuarter > 5) {
    nextStateInput.topOptions[0].col = 12;
  } else {
    nextStateInput.topOptions.push({
      text: TPatEvents.XP_ATTEMPT,
      value: TPatEvents.XP_ATTEMPT,
      color: TColors.INDIGO,
      col: 6,
    });
  }

  if ((!playFlowState.inserting && !playFlowState.editing) || playFlowState.isEditingMostRecentPlay) {
    ctxUpdate.currentWorkflow = TWorkflowType.PAT;
    send(TMachineEvents.TO_PAT, {
      ctxUpdate,
      nextStateInput,
    });
  } else {
    send(TMachineEvents.CONFIRMED, {
      ctxUpdate,
      nextStateInput: PRIMARY_FLOW_INPUTS,
    });
  }
};

export function pushResultType(ctx: Partial<IMachineContext>, resultType: TPlayResultTypes) {
  const updatedResultTypeArrs = cloneDeep(ctx.currentPlay?.resultType || []);
  updatedResultTypeArrs.push(resultType);
  return updatedResultTypeArrs;
}

export const handleFumbleResult = (
  nextEvt: string,
  backEvt: string,
  backToBtnEvt: string,
  payload: INextEvent,
  send: Function,
  playFlowState: ReturnType<typeof usePlayFlowState>,
  generalState: ReturnType<typeof useGeneralState>,
  context: IMachineContext
): void => {
  const isPrePuntSnapPotentialTov =
    context.currentWorkflow === TWorkflowType.PUNT &&
    (!context.currentPlay.playDesignId ||
      context.currentPlay.playDesignId === TPlayDesigns.NO_PLAY ||
      !context.currentPlay.resultType?.length);
  const isKickReturnFumbleLost =
    (context.currentWorkflow === TWorkflowType.PUNT || context.currentWorkflow === TWorkflowType.KICKOFF) &&
    !isPrePuntSnapPotentialTov &&
    payload.value === TPlayResults.FUMBLE_LOST;
  const isKickReturnFumbleRecov =
    (context.currentWorkflow === TWorkflowType.PUNT || context.currentWorkflow === TWorkflowType.KICKOFF) &&
    !isPrePuntSnapPotentialTov &&
    payload.value === TPlayResults.FUMBLE_RECOVERED;

  if (payload.toggles[TPlays.PENALTY_ALERT]) {
    let currentPlay = null;
    if (payload.value === TPlayResults.FUMBLE_LOST) {
      playFlowState.pushEvent(
        {
          eventTypeId: TEventTypes.POTENTIAL_TOV,
          value: true,
        },
        generalState.currentGame.gameId
      );
      currentPlay = {
        ...context.currentPlay,
        resultType: pushResultType(context, TPlayResultTypes.FUMBLE_LOST),
        options: {
          ...context.currentPlay?.options,
          isPrePuntSnapPotentialTov,
          isKickReturnFumbleLost,
          isKickReturnFumbleRecov,
          isTov: true,
        },
      };
    } else if (payload.value === TPlayResults.FUMBLE_RECOVERED) {
      currentPlay = {
        ...context.currentPlay,
        resultType: pushResultType(context, TPlayResultTypes.FUMBLE),
        options: {
          ...context.currentPlay?.options,
          isPrePuntSnapPotentialTov,
          isKickReturnFumbleLost,
          isKickReturnFumbleRecov,
        },
      };
    }

    send(TMachineEvents.PENALTY_SELECTED, {
      ctxUpdate: {
        currentPlay: {
          ...currentPlay,
          options: {
            ...context.currentPlay?.options,
            isTov: payload.value === TPlayResults.FUMBLE_LOST,
            isPrePuntSnapPotentialTov,
            isKickReturnFumbleLost,
            isKickReturnFumbleRecov,
          },
        },
      },
      nextStateInput: {
        ...PENALTY_INPUTS(
          playFlowState.currentTeamRoles.offense,
          generalState.teams[playFlowState.currentTeamRoles.offense].logoUrl,
          playFlowState.currentTeamRoles.defense,
          generalState.teams[playFlowState.currentTeamRoles.defense].logoUrl
        ),
        backTo: TRefEventsActions.BACK_TO_FUMBLE,
      } as ITwoRowGenericSelectorInp,
    });
  }

  switch (payload.value) {
    case TPlayResults.FUMBLE_RECOVERED:
      // recovered
      send(nextEvt, {
        nextStateInput: {
          buttons: YARDAGE_SELECTOR_BUTTONS(),
          sliderLabels: {
            left: generalState.teams[playFlowState.teams.left].abbreviation,
            right: generalState.teams[playFlowState.teams.right].abbreviation,
          },
          backTo: backToBtnEvt,
        },
        ctxUpdate: {
          currentPlay: {
            ...context.currentPlay,
            resultType: pushResultType(context, TPlayResultTypes.FUMBLE),
            options: {
              ...context.currentPlay?.options,
              isPrePuntSnapPotentialTov,
              isKickReturnFumbleLost,
              isKickReturnFumbleRecov,
            },
          },
        } as IMachineContext,
      });
      break;
    case TPlayResults.FUMBLE_LOST:
      send(nextEvt, {
        nextStateInput: {
          buttons: YARDAGE_SELECTOR_BUTTONS(),
          sliderLabels: {
            left: generalState.teams[playFlowState.teams.left].abbreviation,
            right: generalState.teams[playFlowState.teams.right].abbreviation,
          },
          backTo: backToBtnEvt,
        },
        ctxUpdate: {
          currentPlay: {
            ...context.currentPlay,
            resultType: pushResultType(context, TPlayResultTypes.FUMBLE_LOST),
            options: {
              ...context.currentPlay?.options,
              isTov: true,
              isPrePuntSnapPotentialTov,
              isKickReturnFumbleLost,
              isKickReturnFumbleRecov,
            },
          },
        } as IMachineContext,
      });
      break;
    case TPlayResults.NO_FUMBLE:
    default:
      send(backEvt);
      break;
  }
};

// function to handle saftey
export function isSaftey(
  inp: TMetaFuncInput,
  yardageToggleData: any,
  resultType?: TPlayResultTypes
): boolean {
  const { playFlowState, context } = inp;
  const isYardlineAtZero = yardageToggleData.yardLine === 0;

  if (isTurnoverResult(resultType) && resultType !== TPlayResultTypes.TOV) {
    return false;
  }

  const isTov = context.currentPlay.options?.isTov;
  const isKickOffOrPunt =
    context.currentWorkflow === TWorkflowType.KICKOFF ||
    (context.currentWorkflow === TWorkflowType.PUNT &&
      !(context.currentPlay.options?.isPrePuntSnapPotentialTov && !context.currentPlay.options?.isTov));
  const isReturnedFgAttempt = context.currentWorkflow === TWorkflowType.FIELD_GOAL;

  let activeTeamSide =
    playFlowState.teams.left === playFlowState.presentStateBasedOnCurrentMode.activeTeam
      ? TTeamSide.LEFT
      : TTeamSide.RIGHT;

  if (isKickOffOrPunt || isTov || isReturnedFgAttempt) {
    activeTeamSide =
      playFlowState.teams.left === playFlowState.presentStateBasedOnCurrentMode.activeTeam
        ? TTeamSide.RIGHT
        : TTeamSide.LEFT;
  }

  const isOffenseOnTheirOwnGoalLine = activeTeamSide === yardageToggleData.side;

  if (isYardlineAtZero && isOffenseOnTheirOwnGoalLine) {
    return true;
  }
  return false;
}

export function isInDefenceEndZone(inp: TMetaFuncInput, yardageToggleData: any) {
  const { playFlowState } = inp;
  const isYardlineAtZero = yardageToggleData.yardLine === 0;
  const defenseTeamSide =
    playFlowState.teams.left === playFlowState.currentTeamRoles.defense ? TTeamSide.LEFT : TTeamSide.RIGHT;
  const isDefenceSide = defenseTeamSide === yardageToggleData.side;
  return isYardlineAtZero && isDefenceSide;
}

export function doesMeetSackCriteria(inp: TMetaFuncInput, yardageToggleData: any) {
  const { playFlowState, context } = inp;
  const isQbScramble = context.currentPlay.qbScramble;
  const { distanceTraveled } = getResultingDownAndDistance(
    playFlowState.presentStateBasedOnCurrentMode.down,
    playFlowState.presentStateBasedOnCurrentMode.distance,
    playFlowState.presentStateBasedOnCurrentMode.driveDirection,
    playFlowState.presentStateBasedOnCurrentMode.yardLine.number,
    playFlowState.presentStateBasedOnCurrentMode.yardLine.side,
    {
      ...context.currentPlay,
      postPlayYardLine: yardageToggleData.yardLine,
      postPlaySide: yardageToggleData.side,
    }
  );

  return (
    isQbScramble &&
    distanceTraveled < 0 &&
    !getTurnoverStatus(context.currentPlay) &&
    !context.currentPlay.penalty
  );
}

export const isSackWithFumbleLost = (inp: TMetaFuncInput, yardageToggleData: any) => {
  const { playFlowState, context } = inp;
  const isQbScramble = context.currentPlay.qbScramble;
  const { distanceTraveled } = getResultingDownAndDistance(
    playFlowState.presentStateBasedOnCurrentMode.down,
    playFlowState.presentStateBasedOnCurrentMode.distance,
    playFlowState.presentStateBasedOnCurrentMode.driveDirection,
    playFlowState.presentStateBasedOnCurrentMode.yardLine.number,
    playFlowState.presentStateBasedOnCurrentMode.yardLine.side,
    {
      ...context.currentPlay,
      postPlayYardLine: yardageToggleData.yardLine,
      postPlaySide: yardageToggleData.side,
    }
  );

  return (
    isQbScramble &&
    distanceTraveled < 0 &&
    containsFumbleLostResult(context.currentPlay?.resultType || []) &&
    !context.currentPlay.penalty
  );
};

// function to handle CLOSE_1ST toggle
function handleClose1stToggle(inp: TMetaFuncInput, toggles: any) {
  const { playFlowState, generalState } = inp;
  // if close 1st is toggled, add it to the event
  if (toggles[TPlayResults.CLOSE_1ST]) {
    playFlowState.pushEvent(
      {
        eventTypeId: TEventTypes.CLOSE_1ST,
        value: true,
      },
      generalState.currentGame.gameId
    );
  }
}

export function handleYardageToggle(
  inp: TMetaFuncInput,
  backTo: string,
  yardageToggleData: Partial<{
    yardLine: number;
    side: TTeamSide;
    toggles: string[];
    currToggle: number;
    resultType: number;
  }>,
  penalty?: TOffDef,
  isPenaltyDuringReturn?: boolean,
  isPenaltyOnTurnover?: boolean
) {
  const { payload, send, playFlowState, generalState, context } = inp;
  let toggles = [] as string[];

  if (context.yardageToggleData) {
    // runs during toggle state looop
    toggles = context.yardageToggleData.toggles ?? [];
  } else {
    // runs once when entering toggle state loop from yardage selector
    const togglesToSkip: string[] = [TPlayResults.CLOSE_1ST];

    toggles = Object.entries(payload.toggles)
      .filter(([key, value]) => value && !togglesToSkip.includes(key))
      .map(([key]) => key)
      .sort((a, b) => YARDAGE_SELECTOR_TOGGLE_PROPRITY[a] - YARDAGE_SELECTOR_TOGGLE_PROPRITY[b]);

    const { resultType: toggleDataResultType } = yardageToggleData;
    if (
      !context.inputForNextComponent?.title?.includes('Post-') &&
      context.currentWorkflow === TWorkflowType.REGULAR_PLAY &&
      !context.currentPlay.qbScramble &&
      last(context.currentPlay.resultType) !== TPlayResultTypes.INTERCEPTION &&
      toggleDataResultType !== TPlayResultTypes.INTERCEPTION
    ) {
      toggles.push(TPlayResults.PLAYER_ATTRIBUTION);
    }

    handleClose1stToggle(inp, payload.toggles);
  }

  let { resultType } = yardageToggleData;
  const contextUpdate: Partial<IMachineContext> = {
    currentPlay: {
      ...context.currentPlay,
      resultType: [...(context.currentPlay.resultType || []), resultType].filter(
        (id) => id !== null && id !== undefined
      ),
      options: {
        ...context.currentPlay.options,
      },
    },
  };

  if (isPenaltyDuringReturn) {
    contextUpdate.currentPlay.options.isPenaltyDuringReturn = isPenaltyDuringReturn;
  }

  if (penalty) {
    contextUpdate.currentPlay.penalty = penalty;
  }

  if (payload.value === TPlayResults.DECLINED) {
    contextUpdate.currentPlay.penalty = null;
    contextUpdate.currentPlay.penaltySide = null;
    contextUpdate.currentPlay.penaltyYardLine = null;
    yardageToggleData.yardLine = null;
    yardageToggleData.side = null;
    contextUpdate.currentPlay.resultType = contextUpdate.currentPlay.resultType.filter(
      (res) =>
        ![
          TPlayResultTypes.PENALTY_AWAY,
          TPlayResultTypes.PENALTY_HOME,
          TPlayResultTypes.PENALTY_BOTH,
        ].includes(res)
    );

    if (
      playFlowState.driveDirection === contextUpdate.currentPlay.postPlaySide &&
      contextUpdate.currentPlay.postPlayYardLine === 0
    ) {
      contextUpdate.currentPlay.resultType.push(TPlayResultTypes.TD);
    }
  }

  // if result type is not set, set it
  if (!resultType) {
    resultType = last(context.currentPlay.resultType);
  }

  // set post play yardline and side
  contextUpdate.currentPlay.postPlayYardLine =
    yardageToggleData.yardLine ?? contextUpdate.currentPlay.postPlayYardLine;
  contextUpdate.currentPlay.postPlaySide = yardageToggleData.side ?? contextUpdate.currentPlay.postPlaySide;

  if (isPenaltyOnTurnover) {
    yardageToggleData.toggles = yardageToggleData.toggles.filter((t) => t !== TPlayResults.PENALTY);
    return send(TRegularPlayEvents.ON_POSSESSION_SELECT, {
      ctxUpdate: contextUpdate,
      nextStateInput: {
        ...POSSESSION_INPUTS(generalState.teams),
        backTo: TRefEventsActions.BACK_TO_PENALTY,
        title: 'Select Possession',
      },
    });
  }

  const currToggle = toggles[yardageToggleData.currToggle];
  // if no toggle or first is score move to confirmation screen
  if (!currToggle) {
    const nextStateInput: IConfirmationInput = { backTo };

    // if penalty was toggled
    if (
      yardageToggleData.toggles &&
      yardageToggleData.toggles.includes(TPlayResults.PENALTY) &&
      !(
        contextUpdate.currentPlay.penalty === TOffDef.BOTH &&
        contextUpdate.currentPlay.playDesignId === TPlayDesigns.PUNT
      )
    ) {
      return send(TMachineEvents.POST_PENALTY_YARDAGE, {
        ctxUpdate: {
          ...contextUpdate,
          currentPlay: {
            ...contextUpdate.currentPlay,
            penaltyYardLine: yardageToggleData.yardLine,
            penaltySide: yardageToggleData.side,
          },
        },
        nextStateInput: {
          buttons: YARDAGE_SELECTOR_BUTTONS(),
          sliderLabels: {
            left: generalState.teams[playFlowState.teams.left].abbreviation,
            right: generalState.teams[playFlowState.teams.right].abbreviation,
          },
          title: 'Post-Penalty Yardage',
          backTo: TMachineEvents.BACK_TO_HANDLE_YARDAGE_TOGGLE,
        } as IYardageSelectorInput,
      });
    }
    // if penalty offsetting on punt
    if (
      yardageToggleData.toggles &&
      yardageToggleData.toggles.includes(TPlayResults.PENALTY) &&
      contextUpdate.currentPlay.penalty === TOffDef.BOTH &&
      contextUpdate.currentPlay.playDesignId === TPlayDesigns.PUNT
    ) {
      contextUpdate.currentPlay.resultType = [TPlayResultTypes.PENALTY_BOTH];
      contextUpdate.currentPlay.postPlaySide = playFlowState.yardLine.side;
      contextUpdate.currentPlay.postPlayYardLine = playFlowState.yardLine.number;
    }

    if (yardageToggleData.toggles && isTurnoverResult(resultType) && resultType !== TPlayResultTypes.TOV) {
      let newResultTypes = [];

      if (isMultiTovPlay(contextUpdate.currentPlay.resultType)) {
        const firstTov: number = contextUpdate.currentPlay.resultType.find((rt) => isTurnoverResult(rt));
        newResultTypes = [firstTov, resultType];
      } else if (resultType === TPlayResultTypes.FUMBLE_LOST) {
        if (contextUpdate.currentPlay?.playDesignId === TPlayDesigns.PASS) {
          const isSackFumbleLost = isSackWithFumbleLost(
            { ...inp, context: { ...inp.context, currentPlay: contextUpdate.currentPlay } },
            { ...context.yardageToggleData, ...yardageToggleData }
          );

          if (isSackFumbleLost) {
            newResultTypes.push(TPlayResultTypes.SACK_FUMBLE_LOST);
          } else if (contextUpdate.currentPlay.qbScramble) {
            newResultTypes.push(TPlayResultTypes.RUSH_ATTEMPT_FUMBLE_LOST);
          } else {
            newResultTypes.push(TPlayResultTypes.PASS_COMP_FUMBLE_LOST);
          }
        } else if (
          contextUpdate.currentPlay?.playDesignId === TPlayDesigns.RUSH ||
          !!contextUpdate.currentPlay.qbScramble
        ) {
          newResultTypes.push(TPlayResultTypes.RUSH_ATTEMPT_FUMBLE_LOST);
        }
      }

      return send(TMachineEvents.POST_PENALTY_YARDAGE, {
        ctxUpdate: {
          ...contextUpdate,
          currentPlay: {
            ...contextUpdate.currentPlay,
            fumbleYardLine: isFumbleLostResult(resultType) ? yardageToggleData.yardLine : null,
            fumbleSide: isFumbleLostResult(resultType) ? yardageToggleData.side : null,
            resultType: newResultTypes.length > 0 ? newResultTypes : contextUpdate.currentPlay?.resultType,
            options: {
              ...contextUpdate.currentPlay.options,
              isTov: true,
            },
          } as IMachineContextCurrentPlay,
        },
        nextStateInput: {
          buttons: YARDAGE_SELECTOR_BUTTONS(),
          sliderLabels: {
            left: generalState.teams[playFlowState.teams.left].abbreviation,
            right: generalState.teams[playFlowState.teams.right].abbreviation,
          },
          title: 'Post-TOV Yardage',
          backTo: TMachineEvents.BACK_TO_HANDLE_YARDAGE_TOGGLE,
        } as IYardageSelectorInput,
      });
    }

    if (resultType === TPlayResultTypes.FIRST_DOWN && yardageToggleData.yardLine !== 0) {
      nextStateInput.toggleButton = cloneDeep(FIRST_DOWN_BTN);
    }

    let currResultTypeId;
    let isTouchbackTOV = false;
    if (
      yardageToggleData.yardLine === 0 ||
      (!yardageToggleData.yardLine && contextUpdate.currentPlay.postPlayYardLine === 0)
    ) {
      if (isSaftey(inp, { ...context.yardageToggleData, ...yardageToggleData }, resultType)) {
        nextStateInput.toggleButton = cloneDeep(SAFTEY_BTN);
        currResultTypeId = TPlayResultTypes.SAFTEY;
      } else if (
        isTurnoverResult(resultType) &&
        isInDefenceEndZone(inp, yardageToggleData) &&
        context.currentWorkflow === TWorkflowType.REGULAR_PLAY
      ) {
        currResultTypeId = TPlayResultTypes.TOUCHBACK;
        isTouchbackTOV = true;
      } else {
        nextStateInput.toggleButton = cloneDeep(TOUCHDOWN_BTN);
        currResultTypeId = TPlayResultTypes.TD;
      }
    } else if (isTurnoverResult(resultType)) {
      nextStateInput.toggleButton = cloneDeep(TURNOVER_BTN);
    } else if (resultType === TPlayResultTypes.FIRST_DOWN) {
      nextStateInput.toggleButton = cloneDeep(FIRST_DOWN_BTN);
    }

    if (
      isTurnoverResult(resultType) &&
      currResultTypeId !== TPlayResultTypes.TD &&
      currResultTypeId !== TPlayResultTypes.SAFTEY
    ) {
      nextStateInput.toggleButton = cloneDeep(TURNOVER_BTN);
    }

    if (currResultTypeId) {
      contextUpdate.currentPlay.resultType.push(currResultTypeId);
    }
    contextUpdate.currentPlay.options = {
      ...context.currentPlay?.options,
      isTouchbackTov: isTouchbackTOV,
    };

    if (
      doesMeetSackCriteria(
        { ...inp, context: { ...inp.context, currentPlay: contextUpdate.currentPlay } },
        { ...context.yardageToggleData, ...yardageToggleData }
      )
    ) {
      contextUpdate.currentPlay.resultType.push(TPlayResultTypes.SACK);
    }

    return send(TMachineEvents.PLAY_COMPLETED, {
      ctxUpdate: contextUpdate,
      nextStateInput: {
        ...nextStateInput,
        backTo: !toggles.length
          ? TMachineEvents.BACK_TO_YARDAGE_SELECTION
          : TMachineEvents.BACK_TO_HANDLE_YARDAGE,
      },
    });
  }

  let nextStateInput: Partial<ITwoRowGenericSelectorInp> = {};
  contextUpdate.yardageToggleData = {
    ...(yardageToggleData as any),
    toggles,
  };

  // if penalty create nextState inp for penalty
  if (currToggle === TPlayResults.PENALTY) {
    nextStateInput = {
      ...PENALTY_INPUTS(
        playFlowState.currentTeamRoles.offense,
        generalState.teams[playFlowState.currentTeamRoles.offense].logoUrl,
        playFlowState.currentTeamRoles.defense,
        generalState.teams[playFlowState.currentTeamRoles.defense].logoUrl
      ),
      backTo,
    };
  } else if (currToggle === TPlayResults.TURNOVER) {
    nextStateInput = {
      ...TOV_INPUT(
        last(context.currentPlay?.resultType) === TPlayResultTypes.PASS_COMP ||
          context.currentPlay?.playDesignId !== TPlayDesigns.PASS ||
          (context.currentPlay?.resultType ?? []).includes(TPlayResultTypes.INTERCEPTION) ||
          (contextUpdate.currentPlay?.resultType ?? []).includes(TPlayResultTypes.INTERCEPTION)
      ),
      backTo,
    };
  } else if (currToggle === TPlayResults.PLAYER_ATTRIBUTION) {
    nextStateInput = generalState.getPlayerAttributionOptions(
      playFlowState.presentStateBasedOnCurrentMode.activeTeam,
      backTo
    );
  }

  return send(TMachineEvents.TO_HANDLE_YARDAGE_TOGGLE, {
    ctxUpdate: contextUpdate,
    nextStateInput,
  });
}

export function handleInpForYardageToggle(inp: TMetaFuncInput) {
  const { context, payload, playFlowState } = inp;
  let resultType = null;
  let penalty;
  let isPenaltyDuringReturn;
  let isPenaltyOnTurnover;

  const lastToggle = context.yardageToggleData?.toggles?.[context.yardageToggleData.currToggle || 0] ?? null;

  if (lastToggle === TPlayResults.PENALTY) {
    if (payload.value === TTeam.AWAY) {
      resultType = TPlayResultTypes.PENALTY_AWAY;
      penalty = playFlowState.currentTeamRoles.offense === TTeam.AWAY ? TOffDef.OFFENSE : TOffDef.DEFENSE;
    } else if (payload.value === TTeam.HOME) {
      resultType = TPlayResultTypes.PENALTY_HOME;
      penalty = playFlowState.currentTeamRoles.offense === TTeam.HOME ? TOffDef.OFFENSE : TOffDef.DEFENSE;
    } else if (payload.value === TOffDef.BOTH) {
      resultType = TPlayResultTypes.PENALTY_BOTH;
      penalty = TOffDef.BOTH;
    } else if (payload.value === TPlayResults.GROUNDING) {
      resultType =
        playFlowState.currentTeamRoles.offense === TTeam.AWAY
          ? TPlayResultTypes.PENALTY_AWAY
          : TPlayResultTypes.PENALTY_HOME;
      penalty = TOffDef.OFFENSE;
      context.currentPlay.options = {
        ...context.currentPlay.options,
        isIntenionalGrounding: true,
      };
    } else {
      remove(context.yardageToggleData.toggles, (t) => t === TPlayResults.PENALTY);
      context.yardageToggleData.currToggle -= 1;
      context.currentPlay.penalty = null;
    }

    context.currentPlay.refEventOutcomeId = TRefEventOutcomes.PENALTY;

    if (
      (context.currentWorkflow === TWorkflowType.KICKOFF ||
        context.currentWorkflow === TWorkflowType.PUNT ||
        context.currentPlay.options?.isFGReturned) &&
      payload.value !== TPlayResults.DECLINED
    ) {
      isPenaltyDuringReturn = true;
    }

    if (isTurnoverResult(last(context.currentPlay.resultType))) {
      isPenaltyOnTurnover = true;
    }
  } else if (lastToggle === TPlayResults.TURNOVER) {
    resultType = payload.value ? payload.value : null;
  } else if (lastToggle === TPlayResults.PLAYER_ATTRIBUTION) {
    // add player attribution if neccessary
    if (
      (context.currentPlay.playDesignId === TPlayDesigns.RUSH ||
        context.currentPlay.playDesignId === TPlayDesigns.PASS) &&
      (payload.value > 0 || payload.value === null)
    ) {
      context.currentPlay.players = [
        {
          playerId: payload.value,
          roleId:
            context.currentPlay.playDesignId === TPlayDesigns.PASS
              ? TPlayerRole.RECEIVER
              : TPlayerRole.RUSHER,
        },
      ];
    }

    context.currentPlay.options = {
      ...context.currentPlay.options,
      isNonQb1Pass: null,
    };
  }

  handleYardageToggle(
    inp,
    TMachineEvents.BACK_TO_SAME,
    {
      ...context.yardageToggleData,
      resultType,
      currToggle: context.yardageToggleData.currToggle + 1,
    },
    penalty,
    isPenaltyDuringReturn,
    isPenaltyOnTurnover
  );
}

// yardage toggle handle back
export function handleBackForYardageToggle(inp: TMetaFuncInput) {
  const { context, send } = inp;
  send(context.inputForNextComponent.backTo, {
    ctxUpdate: {
      yardageToggleData: {
        ...context.yardageToggleData,
        currToggle: context.yardageToggleData?.currToggle ? context.yardageToggleData.currToggle - 1 : 0,
      },
    } as Partial<IMachineContext>,
  });
}

export const getPostSafetyResultType = (context: IMachineContext) => {
  const resultType = context.currentPlay?.resultType;

  if (containsTurnoverResult(resultType)) {
    const tov = resultType.find((rt) => turnoverResults.includes(rt));
    return [tov, TPlayResultTypes.SAFTEY];
  }

  if (context.currentPlay?.playDesignId === TPlayDesigns.PASS) {
    return resultType.includes(TPlayResultTypes.SACK) || resultType.includes(TPlayResultTypes.PASS_INCOMP)
      ? [TPlayResultTypes.SACK_SAFETY]
      : [TPlayResultTypes.PASS_COMP_SAFETY];
  }
  if (context.currentPlay?.playDesignId === TPlayDesigns.RUSH) {
    return [TPlayResultTypes.RUSH_ATTEMPT_SAFETY];
  }
  if (context.currentPlay?.playDesignId === TPlayDesigns.EXTRA_PT) {
    return [TPlayResultTypes.SAFTEY_1PT];
  }
  return resultType;
};

// start kickoff for saftey
export function handlePostSafteyPlay(inp: TMetaFuncInput) {
  const { send, context, playFlowState } = inp;
  const ctxUpdate: Partial<IMachineContext> = {
    currentPlay: {
      ...context.currentPlay,
      resultType: getPostSafetyResultType(context),
      options: {
        ...context.currentPlay?.options,
        isSaftey: true,
      },
    },
  };

  if ((!playFlowState.inserting && !playFlowState.editing) || playFlowState.isEditingMostRecentPlay) {
    ctxUpdate.currentWorkflow = TWorkflowType.KICKOFF;
    send(TMachineEvents.CONFIRM_WITH_SAFTEY, {
      ctxUpdate,
      nextStateInput: KICKOFF_INPUTS(),
    });
  } else {
    send(TMachineEvents.CONFIRMED, {
      ctxUpdate,
      nextStateInput: PRIMARY_FLOW_INPUTS,
    });
  }
}

// function to handle generic confirmation
export function handleGenericConfirmation(inp: TMetaFuncInput) {
  const { payload, send, context, playFlowState, generalState } = inp;
  const ctxUpdate: IMachineContext = cloneDeep(context);
  const isTov = getTurnoverStatus(context.currentPlay);
  ctxUpdate.currentWorkflow = null;
  ctxUpdate.currentPlay.lateral = !!payload.toggles[TPlays.LATERAL];
  const qb1Toggle = payload.toggles[TAltPlayerOptions.NON_QB_1];

  ctxUpdate.currentPlay.options = {
    ...context.currentPlay?.options,
    isKickoffOrPunt: checkIsKickoffPunt(context),
    isKickoff: context.currentWorkflow === TWorkflowType.KICKOFF,
    isTov,
    isKickReturnFumbleLost:
      (context.currentWorkflow === TWorkflowType.PUNT || context.currentWorkflow === TWorkflowType.KICKOFF) &&
      !context.currentPlay?.options?.isPrePuntSnapPotentialTov &&
      isTov,
    isNonQb1Pass:
      qb1Toggle !== undefined
        ? qb1Toggle
        : !!context.currentPlay?.options?.isNonQb1Pass ||
          ([TPlayDesigns.PASS, TPlayDesigns.RUSH].includes(context.currentPlay.playDesignId) &&
            playFlowState.isQb1Out[playFlowState.activeTeam]),
  };

  // when editing, the touchdown toggle is hidden, so honor the TD already recorded on the play
  const isEditingConfirmedTouchdown =
    playFlowState.editing && last(context.currentPlay.resultType) === TPlayResultTypes.TD;
  const isConfirmedTouchdown = payload.toggles[TPlayResults.TOUCHDOWN] || isEditingConfirmedTouchdown;
  const isConfirmedPuntTouchdown =
    context.currentWorkflow === TWorkflowType.PUNT &&
    context.currentPlay.playDesignId === TPlayDesigns.PUNT &&
    isConfirmedTouchdown;

  if (
    !isConfirmedPuntTouchdown &&
    ctxUpdate.currentPlay.options?.isPossessionRetained === undefined &&
    playFlowState.down === 4 &&
    ([TPlayDesigns.PUNT, TPlayDesigns.FG].includes(ctxUpdate.currentPlay.playDesignId) ||
      ctxUpdate.currentPlay.options?.isTov ||
      // Formation-level penalties have no active play requiring possession selection.
      (ctxUpdate.currentPlay.penalty && !!context.currentWorkflow))
  ) {
    return send(TMachineEvents.ON_SELECT_POSSESSION, {
      ctxUpdate,
      nextStateInput: {
        ...POSSESSION_INPUTS(generalState.teams),
        backTo: TRefEventsActions.BACK_TO_CONFIRMATION,
        title: 'Select Possession',
      },
    });
  }

  if (isConfirmedTouchdown) {
    return handlePostTouchdownPlay(
      { ...inp, context: ctxUpdate },
      context.currentWorkflow === TWorkflowType.KICKOFF || context.currentWorkflow === TWorkflowType.PUNT,
      !!context.currentPlay.options?.isTov
    );
  }

  if (last(context.currentPlay.resultType) === TPlayResultTypes.TD) {
    ctxUpdate.currentPlay.resultType.pop();
  }

  if (payload.toggles[TPlayResults.SAFTEY]) {
    return handlePostSafteyPlay({ ...inp, context: ctxUpdate });
  }

  // if turnover and ending in opponent's endzone should be TD
  if (
    containsTurnoverResult(context.currentPlay.resultType) &&
    context.currentPlay.postPlayYardLine === 0 &&
    playFlowState.teams[context.currentPlay.postPlaySide] ===
      playFlowState.presentStateBasedOnCurrentMode.activeTeam
  ) {
    if (payload.toggles[TPlayResults.TURNOVER]) {
      return handlePostTouchdownPlay({ ...inp, context: ctxUpdate }, false, true);
    }
    return handlePostSafteyPlay({ ...inp, context: ctxUpdate });
  }

  // if first down is toggled and current play is not first down then set it to first down
  if (
    payload.toggles[TPlayResults.FIRST_DOWN] &&
    !context.currentPlay.isFirstDown &&
    !playFlowState.editing
  ) {
    ctxUpdate.currentPlay.isFirstDown = true;
  } else if (payload.toggles[TPlayResults.FIRST_DOWN] === false) {
    // if first down is not toggled and current play is first down then set it to not first down
    ctxUpdate.currentPlay.isFirstDown = false;

    if (last(context.currentPlay.resultType) === TPlayResultTypes.FIRST_DOWN) {
      ctxUpdate.currentPlay.resultType.pop();
    }
  }

  // if turnover toggle if false and the id is set to one of the turnover ids then set it to null
  if (!payload.toggles[TPlayResults.TURNOVER] && isTurnoverResult(last(context.currentPlay.resultType))) {
    ctxUpdate.currentPlay.resultType.pop();
  }

  if (isPreSnapKickoffPenalty(context.currentPlay) || isReKickoffPenalty(context.currentPlay)) {
    return send(TKickoffEvents.BACK_TO_KICKOFF_START, {
      nextStateInput: KICKOFF_INPUTS(),
      ctxUpdate: {
        ...ctxUpdate,
        currentWorkflow: TWorkflowType.KICKOFF,
      },
    });
  }

  return send(TMachineEvents.CONFIRMED, {
    ctxUpdate,
    nextStateInput: PRIMARY_FLOW_INPUTS,
  });
}

export const CONFIRM_BASIC_META = {
  component: TInputTypes.CONFIRMATION_SELECTOR,
  componentConfiguration: {
    title: 'Confirmation',
    showLogo: false,
    showBackBtn: true,
  },
  onNext: handleGenericConfirmation,
  onBack(inp: TMetaFuncInput) {
    const { context, send } = inp;
    send(context.inputForNextComponent.backTo, {});
  },
} as IStateMetaData;

export const HANDLE_YARDAGE_TOGGLES_META = {
  component: TInputTypes.TWO_ROW_GENERIC,
  componentConfiguration: {
    showLogo: true,
    showBackBtn: true,
  },
  onNext: handleInpForYardageToggle,
  onBack: handleBackForYardageToggle,
} as IStateMetaData;

export const startPatWFAlternate = (inp: TMetaFuncInput): void => {
  const { send, playFlowState, context } = inp;
  const ctxUpdate: Partial<IMachineContext> = {
    currentWorkflow: TWorkflowType.PAT,
    currentPlay: {
      ...context.currentPlay,
    },
  };

  const nextStateInput = {
    topOptions: [
      {
        text: TPatEvents.TWO_PT_ATTEMPT,
        value: TPatEvents.TWO_PT_ATTEMPT,
        color: TColors.INDIGO,
        col: 12,
      },
    ] as ISelectionButton[],
    bottomOptions: [
      {
        text: TPlays.REF,
        value: TPlays.REF,
        color: TColors.YELLOW,
        col: 4,
      },
    ] as ISelectionButton[],
    bottomJustify: 'end',
  } as ITwoRowGenericSelectorInp;

  const nextSide =
    playFlowState.teams.left === playFlowState.presentStateBasedOnCurrentMode.activeTeam
      ? TTeamSide.LEFT
      : TTeamSide.RIGHT;
  ctxUpdate.currentPlay.postPlaySide = nextSide;
  ctxUpdate.currentPlay.options = {
    ...context.currentPlay?.options,
    isScore: true,
  };

  send(TMachineEvents.TO_PAT, {
    ctxUpdate,
    nextStateInput,
  });
};

export function determineResumePoint(
  lastPlay: IPlayflowPlay,
  send: Function,
  playFlowState: ReturnType<typeof usePlayFlowState>,
  userState: ReturnType<typeof useUserStore>,
  context: IMachineContext
) {
  if (userState.getUserRole() === TUserRole.CLOCK) {
    return;
  }

  const penaltyResults = [
    TPlayResultTypes.PENALTY_AWAY,
    TPlayResultTypes.PENALTY_BOTH,
    TPlayResultTypes.PENALTY_HOME,
  ];
  const patDesigns = [TPlayDesigns.EXTRA_PT, TPlayDesigns.TWO_PT_CONVERSION];
  if (
    ((patDesigns.includes(lastPlay.playDesignId) && !penaltyResults.includes(lastPlay.playResultTypeId)) ||
      lastPlay.playResultTypeId === TPlayResultTypes.FG ||
      (lastPlay.playDesignId === TPlayDesigns.KICKOFF &&
        penaltyResults.includes(lastPlay.playResultTypeId))) &&
    playFlowState.currentQuarter <= TQuarters.Q4
  ) {
    const yardline =
      lastPlay.playDesignId === TPlayDesigns.KICKOFF && penaltyResults.includes(lastPlay.playResultTypeId)
        ? lastPlay.resultingYardLine
        : KICKOFF_YARDLINE;
    playFlowState.setYardline(yardline, playFlowState.yardLine.side);
    playFlowState.down = null;
    playFlowState.distance = null;
    send(TMachineEvents.RESUME_KICKOFF, {
      nextStateInput: KICKOFF_INPUTS(),
      ctxUpdate: {
        currentWorkflow: TWorkflowType.KICKOFF,
      },
    });
  }
  if (
    lastPlay.playResultTypeId === TPlayResultTypes.SAFTEY ||
    lastPlay.playResultTypeId === TPlayResultTypes.SAFTEY_1PT
  ) {
    playFlowState.setYardline(SAFTEY_KICKOFF_YARDLINE, playFlowState.yardLine.side);
    playFlowState.down = null;
    playFlowState.distance = null;
    send(TMachineEvents.RESUME_KICKOFF, {
      nextStateInput: KICKOFF_INPUTS(),
      ctxUpdate: {
        currentWorkflow: TWorkflowType.KICKOFF,
      },
    });
  } else if (
    touchdownResults.includes(lastPlay.playResultTypeId) ||
    playFlowState.currentQuarter === TQuarters.OT3_PLUS ||
    (patDesigns.includes(lastPlay.playDesignId) && penaltyResults.includes(lastPlay.playResultTypeId))
  ) {
    playFlowState.setYardline(PAT_YARDLINE, playFlowState.yardLine.side);
    playFlowState.down = null;
    playFlowState.distance = null;
    send(TMachineEvents.RESUME_PAT, {
      nextStateInput: PAT_INPUTS,
      ctxUpdate: {
        currentWorkflow: TWorkflowType.PAT,
        currentPlay: {
          ...context.currentPlay,
          options: {
            ...context.currentPlay?.options,
            isScore: true,
          },
        },
      },
    });
  } else {
    send(TMachineEvents.RESUME_REGULAR_PLAY, {
      nextStateInput: PRIMARY_FLOW_INPUTS,
    });
  }
}
